import { type Node, useNodes, useReactFlow, useViewport } from "@xyflow/react";
import { useInViewport, useResponsive } from "ahooks";
import {
  ArrowDown,
  ArrowDownLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpLeft,
  ChevronsUpIcon,
  FullscreenIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from "lucide-react";
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { Button } from "@/components/base/button";

import { type BracketMatch, useBracketContext } from ".";

function ZoomControls({
  controller,
}: {
  controller: ReturnType<typeof useReactFlow>;
}) {
  return (
    <>
      <Button
        radius="full"
        variant="icon"
        className="drop-shadow-xl"
        color="alternate"
        onPress={() => controller.zoomIn({ duration: 250 })}
      >
        <ZoomInIcon size={16} />
      </Button>
      <Button
        radius="full"
        variant="icon"
        className="drop-shadow-xl"
        color="alternate"
        onPress={() => controller.zoomOut({ duration: 250 })}
      >
        <ZoomOutIcon size={16} />
      </Button>
      <Button
        radius="full"
        variant="icon"
        className="drop-shadow-xl"
        color="alternate"
        onPress={() => controller.fitView({ duration: 250 })}
      >
        <FullscreenIcon size={16} />
      </Button>
      <Button
        radius="full"
        variant="icon"
        className="drop-shadow-xl"
        color="alternate"
        tooltip="Scroll to top of page"
        onPress={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        <ChevronsUpIcon size={16} />
      </Button>
    </>
  );
}

function MatchIndexButtons({
  centeredRound,
  closestNodeId,
  roundOfClosestNodeToCenter,
  centerViewportAtNode,
}: {
  centeredRound: BracketMatch[] | null;
  closestNodeId: string | undefined;
  roundOfClosestNodeToCenter: number | null;
  centerViewportAtNode: (roundIdx: number, matchIdx: number) => void;
}) {
  return (
    <>
      {centeredRound?.map((m, i) => (
        <Button
          key={m.id}
          radius="full"
          variant="icon"
          className="drop-shadow-xl"
          color={m.id.toString() === closestNodeId ? "secondary" : "alternate"}
          onPress={() => {
            if (typeof roundOfClosestNodeToCenter === "number") {
              centerViewportAtNode(roundOfClosestNodeToCenter, i);
            }
          }}
        >
          {i + 1}
        </Button>
      ))}
    </>
  );
}

export function Toolbar({
  rounds,
  container,
  children,
}: {
  rounds: BracketMatch[][];
  container: RefObject<HTMLDivElement | null>;
  children: ReactNode;
}) {
  const controller = useReactFlow();

  const viewport = useViewport();
  const nodes = useNodes<Node<BracketMatch>>();
  const size = useResponsive();

  const topPadding = size?.medium ? 100 : 25;

  const closestNodeToCenter = useMemo(() => {
    const bounds = container.current?.getBoundingClientRect();

    if (!bounds) {
      return null;
    }

    // Calculate the focus point in screen coordinates
    // Horizontally: center of viewport
    // Vertically: topPadding distance from top
    const screenFocusX = bounds.width / 2;
    const screenFocusY = topPadding;

    // Convert screen coordinates to flow coordinates
    const flowFocusX = (screenFocusX - viewport.x) / viewport.zoom;
    const flowFocusY = (screenFocusY - viewport.y) / viewport.zoom;

    // Find the node closest to this focus point
    let closestNode: Node<BracketMatch> | null = null;
    let minDistance = Number.POSITIVE_INFINITY;

    for (const node of nodes) {
      const nodeWidth = node.measured?.width ?? node.width ?? 400;

      // Calculate the node's "focus point" (horizontally centered, at the top)
      const nodeFocusX = node.position.x + nodeWidth / 2;
      const nodeFocusY = node.position.y; // Top of the node

      // Calculate distance from viewport focus point to node focus point
      const distance = Math.sqrt(
        (nodeFocusX - flowFocusX) ** 2 + (nodeFocusY - flowFocusY) ** 2,
      );

      if (distance < minDistance) {
        minDistance = distance;
        closestNode = node;
      }
    }

    return closestNode;
  }, [container, nodes, viewport, topPadding]);

  const getNodeCoords = useCallback(
    (id: number) => {
      for (const [r, round] of rounds.entries()) {
        for (const [m, mat] of round.entries()) {
          if (mat.id === id) {
            return [r, m];
          }
        }
      }

      return [null, null];
    },
    [rounds],
  );

  const [roundOfClosestNodeToCenter, matchClosestToCenter] = useMemo(() => {
    if (closestNodeToCenter) {
      return getNodeCoords(closestNodeToCenter.data.id);
    }

    return [null, null];
  }, [closestNodeToCenter, getNodeCoords]);

  const centerViewportAtNodeWithId = useCallback(
    (id: number) => {
      const target = controller.getNode(id.toString());
      const bounds = container.current?.getBoundingClientRect();

      if (target && bounds) {
        // Use measured dimensions if available, otherwise use defaults
        const nodeWidth = target.measured?.width ?? target.width ?? 400;

        const zoom = size?.medium ? 0.8 : 0.55;

        // Calculate the horizontal center (node should be centered horizontally)
        const targetX = target.position.x + nodeWidth / 2;

        // Calculate Y position to place node at top with padding
        // setCenter centers the viewport at the given point, so we need to offset by half the viewport height
        const viewportHeightInFlowCoords = bounds.height / zoom;
        const paddingInFlowCoords = topPadding / zoom;

        // To place the node top at 'topPadding' from viewport top:
        // The center point should be at: nodeTop + (viewportHeight / 2)
        const targetY =
          target.position.y +
          viewportHeightInFlowCoords / 2 -
          paddingInFlowCoords;

        controller.setCenter(targetX, targetY, {
          zoom: zoom,
          duration: 800,
        });
      }
    },
    [controller, topPadding, container, size?.medium],
  );

  const centerViewportAtNode = (roundIdx: number, matchIdx: number) => {
    centerViewportAtNodeWithId(rounds[roundIdx][matchIdx].id);
  };

  const {
    state: { nodeIdToCenter },
    setState,
  } = useBracketContext();

  useEffect(() => {
    if (nodeIdToCenter) {
      setState((state) => ({
        ...state,
        nodeIdToCenter: null,
      }));

      centerViewportAtNodeWithId(nodeIdToCenter);
    }
  }, [nodeIdToCenter, setState, centerViewportAtNodeWithId]);

  const centeredRound =
    roundOfClosestNodeToCenter !== null
      ? rounds[roundOfClosestNodeToCenter]
      : null;

  // Observe the round-buttons bar (a small element) instead of the tall
  // bracket container. The -65% top rootMargin shrinks the observation zone
  // to the bottom 35% of the viewport. Once the round bar scrolls above
  // that line it leaves the zone and `roundBarInView` becomes false,
  // meaning the user has scrolled past most of the content above the
  // bracket — that's when we show the fixed controls.
  const roundBarRef = useRef<HTMLDivElement>(null);
  const [roundBarInView] = useInViewport(roundBarRef, {
    rootMargin: "-65% 0px 0px 0px",
  });
  const controlsVisible = roundBarInView === false;

  return (
    <>
      <div
        ref={roundBarRef}
        className="flex w-full flex-row items-center justify-center space-x-1 overflow-x-scroll bg-gray-300 px-2 py-2 md:space-x-3 md:py-4"
      >
        {rounds.map((r, i) => (
          <Button
            key={r.map((m) => m.id).join(",")}
            color={i === roundOfClosestNodeToCenter ? "secondary" : "alternate"}
            radius="full"
            className="text-xs md:text-sm"
            onPress={() => centerViewportAtNode(i, 0)}
          >
            Round {i + 1}
          </Button>
        ))}
      </div>
      <div className="relative h-full w-full flex-1">
        {children}
        <div className="absolute top-0 right-0 bottom-0 hidden flex-row md:flex">
          <div className="flex h-full flex-col justify-start space-y-3 p-3">
            <ZoomControls controller={controller} />
          </div>
        </div>
        <div className="absolute top-0 bottom-0 left-0 hidden flex-row md:flex">
          <div className="flex h-full flex-col justify-start space-y-3 p-3">
            <MatchIndexButtons
              centeredRound={centeredRound}
              closestNodeId={closestNodeToCenter?.id}
              roundOfClosestNodeToCenter={roundOfClosestNodeToCenter}
              centerViewportAtNode={centerViewportAtNode}
            />
          </div>
        </div>
        {/* Fixed controls: only visible when bracket fills the viewport */}
        {controlsVisible && (
          <>
            {/* Mobile: fixed side controls */}
            <div className="fixed right-0 bottom-14 z-20 flex flex-col space-y-3 p-3 md:hidden">
              <ZoomControls controller={controller} />
            </div>
            <div className="fixed bottom-14 left-0 z-20 flex flex-col space-y-3 p-3 md:hidden">
              <MatchIndexButtons
                centeredRound={centeredRound}
                closestNodeId={closestNodeToCenter?.id}
                roundOfClosestNodeToCenter={roundOfClosestNodeToCenter}
                centerViewportAtNode={centerViewportAtNode}
              />
            </div>
            {/* Bottom navigation bar */}
            <div className="fixed right-0 bottom-0 left-0 z-20 flex flex-row justify-center space-x-3 p-3">
              <Button
                radius="full"
                variant="icon"
                className="drop-shadow-xl"
                color="alternate"
                isDisabled={
                  typeof closestNodeToCenter?.data.teamAPreviousMatchId !==
                  "number"
                }
                onPress={() => {
                  const upLeftNode =
                    closestNodeToCenter?.data.teamAPreviousMatchId;

                  if (upLeftNode) {
                    const [round, match] = getNodeCoords(upLeftNode);

                    if (round !== null && match !== null) {
                      centerViewportAtNode(round, match);
                    }
                  }
                }}
              >
                <ArrowUpLeft size={16} />
              </Button>
              <Button
                radius="full"
                variant="icon"
                className="drop-shadow-xl"
                color="alternate"
                isDisabled={
                  typeof closestNodeToCenter?.data.teamBPreviousMatchId !==
                  "number"
                }
                onPress={() => {
                  const downLeftNode =
                    closestNodeToCenter?.data.teamBPreviousMatchId;

                  if (downLeftNode) {
                    const [round, match] = getNodeCoords(downLeftNode);

                    if (round !== null && match !== null) {
                      centerViewportAtNode(round, match);
                    }
                  }
                }}
              >
                <ArrowDownLeft size={16} />
              </Button>
              <Button
                radius="full"
                variant="icon"
                className="drop-shadow-xl"
                color="alternate"
                isDisabled={
                  !(
                    typeof roundOfClosestNodeToCenter === "number" &&
                    typeof matchClosestToCenter === "number"
                  ) || matchClosestToCenter === 0
                }
                onPress={() => {
                  if (
                    typeof roundOfClosestNodeToCenter === "number" &&
                    typeof matchClosestToCenter === "number"
                  ) {
                    centerViewportAtNode(
                      roundOfClosestNodeToCenter,
                      matchClosestToCenter - 1,
                    );
                  }
                }}
              >
                <ArrowUp size={16} />
              </Button>
              <Button
                radius="full"
                variant="icon"
                className="drop-shadow-xl"
                color="alternate"
                isDisabled={
                  !(
                    typeof roundOfClosestNodeToCenter === "number" &&
                    typeof matchClosestToCenter === "number"
                  ) ||
                  (centeredRound
                    ? matchClosestToCenter === centeredRound.length - 1
                    : true)
                }
                onPress={() => {
                  if (
                    typeof roundOfClosestNodeToCenter === "number" &&
                    typeof matchClosestToCenter === "number"
                  ) {
                    centerViewportAtNode(
                      roundOfClosestNodeToCenter,
                      matchClosestToCenter + 1,
                    );
                  }
                }}
              >
                <ArrowDown size={16} />
              </Button>
              <Button
                radius="full"
                variant="icon"
                className="drop-shadow-xl"
                color="alternate"
                isDisabled={
                  typeof closestNodeToCenter?.data.nextMatchId !== "number"
                }
                onPress={() => {
                  const rightNode = closestNodeToCenter?.data.nextMatchId;

                  if (rightNode) {
                    const [round, match] = getNodeCoords(rightNode);

                    if (round !== null && match !== null) {
                      centerViewportAtNode(round, match);
                    }
                  }
                }}
              >
                <ArrowRight size={16} />
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
